// Delt koblingsflate for kunde-/leverandorreskontro. Backend avgjor alltid om
// 1500/2400-benet, motparten og det ubrukte belopet faktisk kan disponeres.
function ManualPostingSettlementModal({ posting = null, target = null, onClose, onLinked }) {
  const loadItems = () => posting
    ? window.PlummeBackend.getManualPostingSettlementCandidates(posting).filter(item=>item.maxAmount>0.005)
    : window.PlummeBackend.getManualPostingCandidatesForOpenItem(target).filter(item=>item.maxAmount>0.005);
  const [items, setItems] = useState(loadItems);
  const [selected, setSelected] = useState(() => new Set());
  const [amounts, setAmounts] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const targetMode = Boolean(target && !posting);

  useEffect(() => {
    setItems(loadItems());
    setSelected(new Set());
    setAmounts({});
    setError('');
  }, [posting?.id, target?.documentType, target?.documentId]);

  const parseAmount = (value) => window.PlummeMoneyInput.parse(value, { preferDecimal: true });
  const toggle = (item) => {
    setSelected((current) => {
      const next = new Set(targetMode ? [] : current);
      if (!targetMode && next.has(item.id)) next.delete(item.id); else next.add(item.id);
      return next;
    });
    setAmounts((current) => ({ ...current, [item.id]: current[item.id] || String(item.suggestedAmount || item.maxAmount || '') }));
  };
  const chosen = items.filter((item) => selected.has(item.id)).map((item) => ({
    ...item,
    amount: parseAmount(amounts[item.id]),
  }));
  const invalid = chosen.some((item) => !Number.isFinite(item.amount) || item.amount <= 0.005 || item.amount > item.maxAmount + 0.005);
  const save = async () => {
    if (!chosen.length || invalid || saving) return;
    setSaving(true);
    setError('');
    try {
      const postingRef = posting || {
        id: chosen[0].postingId,
        voucherNumber: chosen[0].postingVoucherNumber,
        date: chosen[0].postingDate,
      };
      await window.PlummeBackend.settleManualPostingOpenItems({ posting: postingRef, selections: chosen });
      toast(t(chosen.length === 1 ? 'settlement.linkedOne' : 'settlement.linkedMany', { count: chosen.length }), 'success');
      onLinked?.();
      onClose();
    } catch (cause) {
      setError(cause?.message || t('settlement.linkFailed'));
    } finally {
      setSaving(false);
    }
  };

  return <Modal open portal onClose={saving ? () => {} : onClose} title={t('settlement.title')} max={720}
    footer={<React.Fragment><Button variant="ghost" disabled={saving} onClick={onClose}>{t('common.cancel')}</Button><Button icon="link" disabled={!chosen.length || invalid || saving} onClick={save}>{t(saving ? 'settlement.linking' : 'settlement.linkSelected')}</Button></React.Fragment>}>
    <div className="col gap-14">
      <div className="bg-note info"><Icon name="link" size={18}/><span>{t('settlement.noNewPosting')}</span></div>
      {!items.length && <EmptyState acc="none" noVisual compact title={t('settlement.noMatchesTitle')} text={t('settlement.noMatchesText')}/>}
      {items.map((item) => {
        const isSelected = selected.has(item.id);
        const amount = amounts[item.id] || '';
        return <div key={item.id} className="panel soft" style={{ padding: 14, border: `1px solid ${isSelected ? 'var(--plum-400)' : 'var(--border)'}`, borderRadius: 'var(--r-md)' }}>
          <label className="row gap-12" style={{ alignItems: 'flex-start', cursor: 'pointer' }}>
            <input type={targetMode ? 'radio' : 'checkbox'} name={targetMode ? 'manual-posting-link' : undefined} checked={isSelected} onChange={() => toggle(item)} style={{ marginTop: 4 }}/>
            <span className="grow" style={{ minWidth: 0 }}>
              <span className="row gap-8 wrap"><b>{targetMode ? item.postingVoucherNumber : item.number}</b>{item.exactMatch && <Badge variant="success">{t('settlement.exactMatch')}</Badge>}</span>
              <span className="t-sm muted" style={{ display: 'block', marginTop: 3 }}>
                {targetMode
                  ? `${t('settlement.manualPosting')} · ${item.postingDate || t('settlement.noDate')}`
                  : `${item.partyName} · ${item.documentType === 'invoice' ? t('settlement.customerInvoice') : t('settlement.supplierInvoice')} · ${item.date || t('settlement.noDate')}`}
              </span>
              <span className="t-xs muted" style={{ display: 'block', marginTop: 4 }}>{t('settlement.canLink', { amount: fmtDec(item.maxAmount), available: fmtDec(item.availableAmount) })}</span>
            </span>
          </label>
          {isSelected && <div className="mt-12" style={{ maxWidth: 240 }}><Field label={t('settlement.amount')} help={t('helpTopics.settlement.body')} helpTitle={t('helpTopics.settlement.title')}><Currency value={amount} onChange={(event) => setAmounts((current) => ({ ...current, [item.id]: event.target.value }))}/></Field>
            {parseAmount(amount) > item.maxAmount + 0.005 && <div className="t-xs" style={{ color: 'var(--error)', fontWeight: 700, marginTop: 4 }}>{t('settlement.amountTooHigh')}</div>}
          </div>}
        </div>;
      })}
      {error && <div className="bg-note bad"><Icon name="alertTri" size={18}/><span>{error}</span></div>}
    </div>
  </Modal>;
}

function ManualPostingSettlementPanel({ postings }) {
  const entries = (Array.isArray(postings) ? postings : [postings])
    .filter((posting) => !(posting?.lines || []).some(line=>line.supplierInvoiceId))
    .map((posting) => posting?.savedEntry || posting)
    .filter((posting) => posting?.id)
    .filter((posting) => window.PlummeBackend.getManualPostingSettlementCandidates(posting).length > 0);
  const [active, setActive] = useState(null);
  if (!entries.length) return null;
  return <React.Fragment>
    <div className="fp-receipt-card mt-16">
      <div className="fp-receipt-head"><div><b className="t-h3" style={{ fontSize: 14 }}>{t('settlement.cardTitle')}</b><div className="t-sm muted" style={{ marginTop: 3 }}>{t('settlement.cardText')}</div></div></div>
      <div className="col gap-8 mt-12">{entries.map((entry) => <Button key={entry.id} variant="secondary" icon="link" onClick={() => setActive(entry)} data-help={t('helpTopics.settlement.body')} data-help-title={t('helpTopics.settlement.title')}>{t('settlement.linkPosting', { voucher: entry.voucherNumber || t('settlement.manualPosting').toLowerCase() })}</Button>)}</div>
    </div>
    {active && <ManualPostingSettlementModal posting={active} onClose={() => setActive(null)}/>}
  </React.Fragment>;
}

Object.assign(window, { ManualPostingSettlementModal, ManualPostingSettlementPanel });
